import { useState } from 'react';
import { Forward, MoreHorizontal, Merge, UserPlus, Video } from 'lucide-react';
import type { UserRef } from '@cyphex/types';
import { Dropdown, type DropdownItem } from '@cyphex/ui';
import { ControlButton } from '@/features/meeting/components/ControlButton';
import type { CallActions } from '../../hooks/useCallActions';
import { labelForActiveCall, useOtherActiveCalls } from '../../hooks/useOtherActiveCalls';
import { CallPeoplePicker } from './CallPeoplePicker';

export interface CallMoreMenuProps {
  callId: string;
  actions: CallActions;
  /** Already live on this call — never offered as a transfer or add target. */
  presentUserIds: readonly string[];
  disabled: boolean;
  /**
   * SOMEBODY ELSE HAS ACTUALLY ANSWERED.
   *
   * Only "Move to meeting" reads it, and it is the one verb here that is
   * destructive before anyone picks up: escalating a call that is still ringing
   * moves a party of ONE into a meeting and cancels the callee's phone as a
   * missed call — no invite, no navigation, nothing on their screen. The rest of
   * this menu is fine mid-ring (transferring or adding people to a call nobody
   * has answered is a real thing to want), which is why this gates the ITEM and
   * not the trigger.
   *
   * The server refuses the same case inside the escalate transaction
   * (`escalationBlocker`), so this is the courtesy, not the enforcement.
   */
  canEscalate: boolean;
}

type Picker = 'none' | 'transfer' | 'add';

/**
 * TRANSFER, ADD PEOPLE, MERGE, MOVE TO MEETING.
 *
 * All four had working REST routes (merge's was built in this wave) and no
 * control anywhere in the product — the call toolbar offered mute, camera,
 * share, hold, devices and hang up, and nothing else. They live behind one
 * "More" affordance rather than four more buttons because the call bar is the
 * narrowest toolbar in the app and these are the rarest verbs on it.
 */
export function CallMoreMenu(props: CallMoreMenuProps) {
  const [open, setOpen] = useState(false);
  const [picker, setPicker] = useState<Picker>('none');
  /* Only asked while the menu is open: it is a live question whose answer is
     worthless a minute later. */
  const otherCalls = useOtherActiveCalls(props.callId, open);

  const items: DropdownItem[] = [
    {
      id: 'transfer',
      label: 'Transfer',
      icon: Forward,
      onSelect: () => setPicker('transfer'),
    },
    {
      id: 'add',
      label: 'Add people',
      icon: UserPlus,
      onSelect: () => setPicker('add'),
    },
    /* MERGE IS CONDITIONAL. It can only mean something when there genuinely is
       a second call, so when there is not, the option is absent rather than
       present and failing. */
    ...otherCalls.map((call) => ({
      id: `merge-${call.callId}`,
      label: `Merge with ${labelForActiveCall(call)}`,
      icon: Merge,
      separatorBefore: true,
      onSelect: () => props.actions.merge(call.callId),
    })),
    {
      id: 'escalate',
      label: 'Move to meeting',
      icon: Video,
      separatorBefore: true,
      disabled: !props.canEscalate,
      onSelect: () => props.actions.escalate(),
    },
  ];

  return (
    <>
      <Dropdown
        open={open}
        onOpenChange={setOpen}
        side="top"
        align="center"
        label="Call actions"
        items={items}
        trigger={
          <ControlButton
            icon={MoreHorizontal}
            label="More"
            aria-label="More call actions"
            hasMenu
            disabled={props.disabled}
          />
        }
      />

      <CallPeoplePicker
        open={picker === 'transfer'}
        onOpenChange={(next) => setPicker(next ? 'transfer' : 'none')}
        title="Transfer this call"
        description="Hand the call over now, or speak to them first."
        confirmLabel="Transfer now"
        excludeUserIds={props.presentUserIds}
        busy={props.actions.isBusy}
        onConfirm={(person: UserRef) => {
          setPicker('none');
          props.actions.transfer({
            mode: 'blind',
            targetUserId: person.id,
            targetName: person.displayName,
          });
        }}
        secondary={{
          label: 'Consult first',
          onSelect: (person: UserRef) => {
            setPicker('none');
            props.actions.transfer({
              mode: 'consultative',
              targetUserId: person.id,
              targetName: person.displayName,
            });
          },
        }}
      />

      <CallPeoplePicker
        open={picker === 'add'}
        onOpenChange={(next) => setPicker(next ? 'add' : 'none')}
        title="Add people"
        description="They are rung on their own settings, like any other call."
        confirmLabel="Add to call"
        excludeUserIds={props.presentUserIds}
        busy={props.actions.isBusy}
        onConfirm={(person: UserRef) => {
          setPicker('none');
          props.actions.addPeople([person.id]);
        }}
      />
    </>
  );
}
